import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { Tag } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import './TrendChart.css';

interface TrendDataPoint {
  period: string;
  week_number: string;
  [key: string]: any;
}

interface TrendChartProps {
  data: TrendDataPoint[];
  metrics: string[];
  chartType?: 'line' | 'bar' | 'area';
  showComparison?: boolean;
}

const METRIC_COLORS: Record<string, string> = {
  market_control: '#1890ff',
  replacement_rate: '#52c41a',
  position_retention: '#faad14',
  conversion_advantage: '#722ed1',
  brand_penetration: '#eb2f96',
  brand_concentration: '#13c2c2',
  brand_diversity: '#fa8c16',
  brand_stability: '#2f54eb',
  market_opportunity: '#f5222d',
};

const METRIC_NAMES: Record<string, string> = {
  market_control: '市场控制度',
  replacement_rate: '真实替换率',
  position_retention: '位置保持率',
  conversion_advantage: '转化优势系数',
  brand_penetration: '品牌渗透指数',
  brand_concentration: '品牌集中度',
  brand_diversity: '品牌多样性',
  brand_stability: '品牌稳定性',
  market_opportunity: '市场机会指数',
};

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  metrics,
  chartType = 'line',
  showComparison = true,
}) => {
  const calculateChange = (current: number, previous: number): { value: number; isPositive: boolean } => {
    if (!previous) return { value: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0,
    };
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const xAxisProps = {
      dataKey: 'week_number',
      tick: { fontSize: 12 },
    };

    const yAxisProps = {
      tick: { fontSize: 12 },
    };

    const tooltipProps = {
      contentStyle: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
      },
    };

    const legendProps = {
      wrapperStyle: { fontSize: 12 },
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {metrics.map((metric) => (
              <Bar
                key={metric}
                dataKey={metric}
                fill={METRIC_COLORS[metric] || '#8884d8'}
                name={METRIC_NAMES[metric] || metric}
              />
            ))}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {metrics.map((metric) => (
              <Area
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={METRIC_COLORS[metric] || '#8884d8'}
                fill={METRIC_COLORS[metric] || '#8884d8'}
                fillOpacity={0.3}
                name={METRIC_NAMES[metric] || metric}
              />
            ))}
          </AreaChart>
        );

      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {metrics.map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={METRIC_COLORS[metric] || '#8884d8'}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                name={METRIC_NAMES[metric] || metric}
              />
            ))}
          </LineChart>
        );
    }
  };

  const renderComparison = () => {
    if (!showComparison || data.length < 2) return null;

    const latest = data[data.length - 1];
    const previous = data[data.length - 2];

    return (
      <div className="trend-comparison">
        <h4>周环比变化</h4>
        <div className="comparison-grid">
          {metrics.map((metric) => {
            const currentValue = latest[metric];
            const previousValue = previous[metric];
            const change = calculateChange(currentValue, previousValue);

            return (
              <div key={metric} className="comparison-item">
                <div className="comparison-label">{METRIC_NAMES[metric] || metric}</div>
                <div className="comparison-value">
                  <span className="current-value">
                    {typeof currentValue === 'number' ? currentValue.toFixed(1) : currentValue}
                    {metric !== 'conversion_advantage' && '%'}
                  </span>
                  <Tag
                    color={change.isPositive ? 'green' : 'red'}
                    icon={change.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  >
                    {change.value.toFixed(1)}%
                  </Tag>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="trend-chart-container">
      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>
      {renderComparison()}
    </div>
  );
};

